import type { ExperienceHeatLevel, MissionHeatmapLocationInput } from "./missionHeatmapDisplay";

export const experienceHeatmapMinRadiusMiles = 6;
export const experienceHeatmapMaxRadiusMiles = 30;

type RankableLocation = Readonly<{
  locationKey: string;
  cityLabel: string;
  latitude: number;
  longitude: number;
  missionCount: number;
}>;

export function experiencePercentileScoreFromRank(
  rank: number,
  totalLocations: number,
): number {
  if (totalLocations <= 0) {
    return 0;
  }
  return Math.round(((totalLocations - rank + 1) / totalLocations) * 1000) / 10;
}

export function experienceTierFromPercentileScore(score: number): Readonly<{
  heatLevel: ExperienceHeatLevel;
  percentileLabel: string;
}> {
  if (score >= 90) {
    return { heatLevel: "top_10", percentileLabel: "Top 10%" };
  }
  if (score >= 75) {
    return { heatLevel: "top_25", percentileLabel: "Top 25%" };
  }
  if (score >= 50) {
    return { heatLevel: "top_50", percentileLabel: "Top 50%" };
  }
  return { heatLevel: "bottom_50", percentileLabel: "Bottom 50%" };
}

export function experienceRadiusMiles(
  missionCount: number,
  maxMissionCount: number,
): number {
  if (maxMissionCount <= 0) {
    return experienceHeatmapMinRadiusMiles;
  }
  const normalized = missionCount / maxMissionCount;
  const radius = experienceHeatmapMinRadiusMiles
    + normalized * (experienceHeatmapMaxRadiusMiles - experienceHeatmapMinRadiusMiles);
  return Math.round(
    Math.min(experienceHeatmapMaxRadiusMiles, Math.max(experienceHeatmapMinRadiusMiles, radius)) * 10,
  ) / 10;
}

function heatOpacityWithinTier(
  missionCount: number,
  tierLocations: readonly RankableLocation[],
): number {
  if (tierLocations.length <= 1) {
    return 1;
  }
  const counts = tierLocations.map((location) => location.missionCount);
  const maxCount = Math.max(...counts);
  const minCount = Math.min(...counts);
  if (maxCount === minCount) {
    return 0.85;
  }
  const normalized = (missionCount - minCount) / (maxCount - minCount);
  return 0.55 + normalized * 0.45;
}

function roundPortfolioPercentage(numerator: number, denominator: number): number {
  if (denominator <= 0) {
    return 0;
  }
  return Math.round((numerator / denominator) * 1000) / 10;
}

function competitionRankForMissionCount(
  missionCount: number,
  sortedCountsDesc: readonly number[],
): number {
  return sortedCountsDesc.findIndex((count) => count === missionCount) + 1;
}

export function locationHasExperienceRanking(
  location: MissionHeatmapLocationInput,
): boolean {
  return Boolean(
    location.heatLevel
    && location.heatLevel !== "none"
    && location.percentileLabel
    && location.portfolioPercentage !== undefined
    && location.experiencePercentile !== undefined
    && location.heatOpacity !== undefined
    && location.radiusMiles !== undefined,
  );
}

export function calculateExperienceRanking(
  locations: readonly RankableLocation[],
): readonly MissionHeatmapLocationInput[] {
  if (locations.length === 0) {
    return [];
  }

  const sorted = [...locations].sort((left, right) =>
    right.missionCount - left.missionCount
    || left.cityLabel.localeCompare(right.cityLabel),
  );
  const totalMissions = sorted.reduce((sum, location) => sum + location.missionCount, 0);
  const totalLocations = sorted.length;
  const missionCounts = sorted.map((location) => location.missionCount);
  const uniqueCountsDesc = [...new Set(missionCounts)].sort((left, right) => right - left);
  const maxMissionCount = missionCounts[0] ?? 0;
  const tierGroups = new Map<ExperienceHeatLevel, RankableLocation[]>();

  const ranked = sorted.map((location) => {
    const rank = competitionRankForMissionCount(location.missionCount, uniqueCountsDesc);
    const experiencePercentile = experiencePercentileScoreFromRank(rank, totalLocations);
    const { heatLevel, percentileLabel } = experienceTierFromPercentileScore(experiencePercentile);
    const tierLocations = tierGroups.get(heatLevel) ?? [];
    tierLocations.push(location);
    tierGroups.set(heatLevel, tierLocations);

    return {
      locationKey: location.locationKey,
      cityLabel: location.cityLabel,
      latitude: location.latitude,
      longitude: location.longitude,
      missionCount: location.missionCount,
      portfolioPercentage: roundPortfolioPercentage(location.missionCount, totalMissions),
      experiencePercentile,
      percentileLabel,
      heatLevel,
      heatOpacity: heatOpacityWithinTier(location.missionCount, tierLocations),
      radiusMiles: experienceRadiusMiles(location.missionCount, maxMissionCount),
    } satisfies MissionHeatmapLocationInput;
  });

  return ranked.map((location) => ({
    ...location,
    heatOpacity: heatOpacityWithinTier(
      location.missionCount,
      tierGroups.get(location.heatLevel ?? "none") ?? [location],
    ),
  }));
}

export function ensureRankedHeatmapLocations(
  locations: readonly MissionHeatmapLocationInput[],
): readonly MissionHeatmapLocationInput[] {
  if (locations.length === 0) {
    return locations;
  }
  if (locations.every(locationHasExperienceRanking)) {
    return locations;
  }
  return calculateExperienceRanking(locations);
}
